import React from 'react';
import { designTokens, getStatusBadgeClass, formatStatusText } from '../../theme/designTokens';

export interface BadgeProps {
  /**
   * Status value (automatically styled based on status)
   * Examples: 'active', 'inactive', 'in_progress', 'completed', etc.
   */
  status: string;

  /**
   * Icon to display before the text
   */
  icon?: React.ReactNode;

  /**
   * Custom text to display (defaults to formatted status)
   */
  children?: React.ReactNode;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Badge Component
 *
 * Displays status indicators with consistent styling.
 * Automatically applies correct colors based on status value.
 *
 * @example
 * ```tsx
 * <Badge status="active" />
 * <Badge status="in_progress" icon={<Clock />} />
 * <Badge status="completed">Done</Badge>
 * ```
 */
export const Badge: React.FC<BadgeProps> = ({ status, icon, children, className = '' }) => {
  const badgeClass = getStatusBadgeClass(status);
  const displayText = children || formatStatusText(status);

  const finalClassName = `
    ${designTokens.badges.base}
    ${badgeClass}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <span className={finalClassName}>
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span>{displayText}</span>
    </span>
  );
};

export default Badge;
